import { encodeEmail } from '@imprint-payments/imprint-lib';

import { RESPONSE_CODE } from '../api/apiConstants';
import { getBaseUrl } from '../api/http/getRequestMetadata';
import { HEADERS, RequestType } from '../api/http/httpConsts';
import { setDeviceID } from '../app/apiScreenSlice';
import { store } from '../app/store';
import { getTodaysDateInNumbers } from './formatUtils';
import { getLanguageFromCookie } from './functionUtils';
import { getDeviceID, getSignature } from './headers';

const newUUID =
  Date.now().toString(36) + Math.random().toString(36).substring(2);

const GENERIC_HEADER = {
  'x-imprint-platform': 'web',
  'x-imprint-device-id': getDeviceID(),
} as HeadersInit;

const RESPONSE_HEADERS: Array<string> = ['x-imprint-delay-in-ms'];

export enum METHOD {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
}

interface RequestParams {
  url: string;
  token?: string;
  method?: METHOD;
  body?: any;
  headers?: boolean;
  keepAlive?: boolean;
  deviceID?: string;
}

export const Request = async ({
  url,
  method = METHOD.GET,
  token,
  body,
  headers,
  keepAlive = false,
  deviceID,
}: RequestParams) => {
  const tokenHeader =
    token == null
      ? undefined
      : ({ 'x-imprint-access-token': `${token}` as string } as HeadersInit);

  const signature = await getSignature(url, body);

  if (store.getState().apiScreenScreenSelector.deviceID === '') {
    store.dispatch(setDeviceID(newUUID));
  }

  deviceID = store.getState().apiScreenScreenSelector.deviceID;

  const languageHeader = {
    'Accept-Language': getLanguageFromCookie() as string,
  } as HeadersInit;

  const signatureHeader =
    signature === ''
      ? ''
      : ({
          'x-imprint-signature': `${signature}` as string,
        } as HeadersInit);

  const deviceIDHeader =
    deviceID === ''
      ? ''
      : ({
          'x-imprint-device-id': `${deviceID}` as string,
        } as HeadersInit);

  const todayDate = getTodaysDateInNumbers();
  const webBuildHeader = {
    'x-imprint-web-build': `${todayDate}` as string,
  } as HeadersInit;

  const clientSecret = store.getState().apiScreenScreenSelector.clientSecret;
  const clientSecretHeader = {
    [HEADERS.clientSecret]: clientSecret,
  };

  const sessionId = store.getState().apiScreenScreenSelector.sessionID;
  const sessionIdHeader = {
    [HEADERS.sessionId]: sessionId,
  };

  const telemetryID =
    store.getState().apiScreenScreenSelector.telemetryID || '';
  const telemetryHeader = {
    [HEADERS.telemetryID]: telemetryID,
  };

  return new Promise((resolve, reject) => {
    fetch(`${getBaseUrl(store, RequestType.INTERNAL)}${url}`, {
      headers: {
        ...tokenHeader,
        ...signatureHeader,
        ...GENERIC_HEADER,
        ...deviceIDHeader,
        ...languageHeader,
        ...webBuildHeader,
        ...(clientSecret && !clientSecret.startsWith('demo_')
          ? clientSecretHeader
          : {}),
        ...sessionIdHeader,
        ...telemetryHeader,
      },
      method,
      keepalive: keepAlive,
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        if (!res.ok) {
          const errorBody = await res.json();
          // The error object should contain status and body. However, some legacy code might
          // be using error.code from the body. Keep compatibility by adding __httpStatus
          (errorBody as any).__httpStatus = res.status;
          throw errorBody;
        }
        if (headers) {
          const headerObj: { [key: string]: string } = {};
          res.headers.forEach((val, key) => {
            if (RESPONSE_HEADERS.includes(key)) {
              headerObj[key] = val;
            }
          });

          const val = {
            headers: headerObj,
            body: await res.json(),
          };
          return val;
        }
        return res.status !== RESPONSE_CODE.NO_CONTENT ? res.json() : res;
      })
      .then((data) => {
        resolve(data);
      })
      .catch((e) => {
        return reject(e);
      });
  });
};

interface AssetsRequestParams {
  url: string;
  image?: boolean;
}

export const requestAssests = ({ url, image = false }: AssetsRequestParams) => {
  return new Promise((resolve, reject) => {
    fetch(`${getBaseUrl(store, RequestType.EXTERNAL)}${url}`)
      .then(async (res) => {
        if (!res.ok) {
          throw await res;
        }
        if (image) {
          const imageBlob = await res.blob();
          const imageObjectURL = URL.createObjectURL(imageBlob);
          return imageObjectURL;
        }
        return res.json();
      })
      .then((data) => resolve(data))
      .catch((e) => {
        return reject(e);
      });
  });
};

interface ApplicationsParams {
  url: string;
  token?: string;
  method?: METHOD;
  productUUID: string;
  email: string;
}

export const ApplicationEligibilityRequest = async ({
  url,
  method = METHOD.GET,
  token,
  productUUID,
  email,
}: ApplicationsParams) => {
  const tokenHeader =
    token == null
      ? undefined
      : ({ 'x-imprint-access-token': `${token}` as string } as HeadersInit);

  const signature = await getSignature(`${url}/${productUUID}/eligibility`);

  const signatureHeader =
    signature === ''
      ? undefined
      : ({
          'x-imprint-signature': `${signature}` as string,
        } as HeadersInit);

  const encodedEmail = encodeEmail(email);

  return new Promise((resolve, reject) => {
    fetch(
      `${getBaseUrl(
        store,
        RequestType.INTERNAL
      )}${url}/${productUUID}/eligibility?email=${encodedEmail}`,
      {
        headers: {
          ...tokenHeader,
          ...signatureHeader,
          ...GENERIC_HEADER,
        },
        method,
      }
    )
      .then(async (res) => {
        if (!res.ok) {
          throw await res.json();
        }
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((e) => {
        return reject(e);
      });
  });
};

interface RejectionPDFParams {
  url: string;
  token: string;
  method?: METHOD;
  applicationUUID: string;
}

export const RejectionPDFRequest = async ({
  url,
  method = METHOD.GET,
  token,
  applicationUUID,
}: RejectionPDFParams) => {
  const tokenHeader =
    token == null
      ? undefined
      : ({ 'x-imprint-access-token': `${token}` as string } as HeadersInit);

  const signature = await getSignature(`${url}/${applicationUUID}/reasons`);

  const signatureHeader =
    signature === ''
      ? undefined
      : ({
          'x-imprint-signature': `${signature}` as string,
        } as HeadersInit);

  // TODO: investigate rejection reason auth issue from here
  return new Promise((resolve, reject) => {
    fetch(
      `${getBaseUrl(
        store,
        RequestType.INTERNAL
      )}${url}/${applicationUUID}/reasons`,
      {
        headers: {
          ...tokenHeader,
          ...signatureHeader,
          ...GENERIC_HEADER,
        },
        method,
      }
    )
      .then(async (res) => {
        if (!res.ok) {
          throw await res.json();
        }
        return res.json();
      })
      .then((data) => {
        resolve(data);
      })
      .catch((e) => {
        return reject(e);
      });
  });
};
